import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './styles';
import { IHardToPlaceDonor } from '@nkr/models/index';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Glyphicon } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: IHardToPlaceDonor[],
    isAdmin: boolean
}

const HardToPlaceDonorsGrid = (props: IProps) => {
    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = e.columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        e.api.sizeColumnsToFit();
    };

    const columns: ColDef[] = [
        {
            colId: 'Rank',
            field: 'rank',
            headerName: 'Rank',
            sortable: false,
            width: 50,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
        },
        {
            colId: 'donorCenterAlias',
            field: 'donorCenterAlias',
            headerName: 'Center',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Alias',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IHardToPlaceDonor;

                if (props.isAdmin) {
                    return <a href={`/center/${data.donorCenterId}/donor/${data.donorId}/profile/medical-details`} target='_blank'>{data.donorAlias}</a>;
                }

                return <>{data.donorAlias}</>;
            },
        },
        {
            colId: 'transplantDate',
            field: 'transplantDate',
            headerName: 'Transplant Date',
            width: 110,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Transplant<br/>Date</SortableHeaderComponent>,
            comparator: Utility.dateComparator,
        },
        {
            colId: 'ageAtTimeOfTransplant',
            field: 'ageAtTimeOfTransplant',
            headerName: 'Donor Age',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Age<br/>(&gt;= 65)</SortableHeaderComponent>,
            cellStyle: (params) => {
                let data = params.data as IHardToPlaceDonor;

                if (data.ageAtTimeOfTransplant >= 65) {
                    return { backgroundColor: '#5CB85C' }
                }

                return null;
            },
        },
        {
            colId: 'meetsLeftAnatomyCriteria',
            field: 'meetsLeftAnatomyCriteria',
            headerName: 'Left Kidney Complex Anatomy',
            width: 130,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Complex Anatomy <br /> {`(L >= 3 Arteries)`} </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IHardToPlaceDonor;

                if (data.whichKidneyDonated == 'Left' && data.numberOfArteries >= 3) {
                    return `L${data.numberOfArteries}`
                }

                return '';
            },
            cellStyle: (params) => {
                let data = params.data as IHardToPlaceDonor;

                if (data.meetsLeftAnatomyCriteria) {
                    return { backgroundColor: '#5CB85C' }
                }

                return null;
            },
        },
        {
            colId: 'meetsRightAnatomyCriteria',
            field: 'meetsRightAnatomyCriteria',
            headerName: 'Right Kidney Complex Anatomy',
            width: 130,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Complex Anatomy <br /> {`(R >= 2 Arteries)`} </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IHardToPlaceDonor;

                if (data.whichKidneyDonated == 'Right' && data.numberOfArteries >= 2) {
                    return `R${data.numberOfArteries}`
                }

                return '';
            },
            cellStyle: (params) => {
                let data = params.data as IHardToPlaceDonor;

                if (data.meetsRightAnatomyCriteria) {
                    return { backgroundColor: '#5CB85C' }
                }

                return null;
            },
        },
        {
            colId: 'creatinineClearance',
            field: 'creatinineClearance',
            headerName: 'Creatinine Clearance',
            width: 110,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Kidney Function <br /> {`(CC < 80)`} </SortableHeaderComponent>,
            cellStyle: (params) => {
                let data = params.data as IHardToPlaceDonor;

                if (data.meetsCCCriteria) {
                    return { backgroundColor: '#5CB85C' }
                }

                return null;
            },
        },
        {
            colId: 'meetsABOCriteria',
            field: 'meetsABOCriteria',
            headerName: 'Donor Blood Type',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> AB<br/>Donor</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IHardToPlaceDonor;

                if (data.meetsABOCriteria) {
                    return <Glyphicon glyph='ok' style={{ color: '#5CB85C' }} />;
                }

                return ''
            },
        },
        {
            colId: 'isRealTimeSwapFailure',
            field: 'isRealTimeSwapFailure',
            headerName: 'RTSF',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>RTSF Cases</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IHardToPlaceDonor;

                if (data.isRealTimeSwapFailure) {
                    return <Glyphicon glyph='ok' style={{ color: '#5CB85C' }} />;
                }

                return ''
            },
        },
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <>
            <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' fontSize='14px' height={'40vh'}>
                <AgGridReact
                    enableCellTextSelection={true}
                    enableRangeSelection={true}
                    columnDefs={columnDefs}
                    defaultColDef={{
                        cellClass: 'center-list-cell',
                        headerClass: 'center-list-header',
                        sortable: true
                    }}
                    rowData={rowData}
                    headerHeight={58}
                    noRowsOverlayComponentFramework={() =>
                        <div>No data for the selected date range...</div>}
                    onGridSizeChanged={onGridSizeChanged}
                    onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                    suppressCellSelection={true}
                    onSortChanged={(e: SortChangedEvent) => {
                        e.api.refreshCells();
                    }}
                />
            </GridWrapper>
        </>
    )
}

export default HardToPlaceDonorsGrid;